import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  FormLabel,
  Textarea,
  Flex,
  Text,
  HStack,
  IconButton,
  Button,
  Image,
  FormErrorMessage,
  useTheme,
} from "@chakra-ui/react";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
import SubmitButton from "../../buttons/SubmitButton";
import { Select } from "chakra-react-select";
import { getAllSubjects } from "../../../store/slice/subjectSlice";
import {
  createMentor,
  updateMentor,
} from "../../../store/slice/teacherManagementSlice";
import { useDispatch, useSelector } from "react-redux";
import { MdDeleteOutline } from "react-icons/md";
import { IoAddOutline } from "react-icons/io5";

const AddTeacherModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const { addNotification } = useToastContext();
  const { primaryBlueLight, primaryBlue } = theme.colors.pallete;
  const dispatch = useDispatch();
  const { subjects } = useSelector((state) => state.subject);

  const initialState = {
    subjectId: "",
    tags: [""],
    name: "",
    email: "",
    password: "",
    bio: "",
    experienceYears: "",
    workExperiences: [
      { organization: "", startYear: "", endYear: "", isCurrent: false },
    ],
    highlights: "",
    displayOrder: "",
    files: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // Prefill form when editing
  useEffect(() => {
    if (isOpen && editData) {
      setFormData({
        subjectId: editData.subjectId || "",
        tags:
          Array.isArray(editData.tags) && editData.tags.length
            ? editData.tags
            : [""],
        name: editData.name || "",
        email: editData.email || "",
        password: "",
        bio: editData.bio || "",
        experienceYears: editData.experienceYears?.toString() || "",
        workExperiences: Array.isArray(editData.workExperience)
          ? editData.workExperience.map((we) => ({
              organization: we.organization || "",
              startYear: we.startYear?.toString() || "",
              endYear: we.endYear?.toString() || "",
              isCurrent: !!we.isCurrent,
            }))
          : [
              {
                organization: "",
                startYear: "",
                endYear: "",
                isCurrent: false,
              },
            ],
        highlights: Array.isArray(editData.highlights)
          ? editData.highlights.join("\n")
          : "",
        displayOrder: editData.displayOrder?.toString() || "",
        files: editData.files || "",
      });
    }
  }, [isOpen, editData]);

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialState);
      setErrors({});
    }
  }, [isOpen]);

  // Load subjects list when modal opens
  useEffect(() => {
    if (isOpen) dispatch(getAllSubjects());
  }, [isOpen, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Tag handlers
  const handleTagChange = (index, value) => {
    const tags = [...formData.tags];
    tags[index] = value;
    setFormData((prev) => ({ ...prev, tags }));
    setErrors((prev) => ({ ...prev, tags: "" }));
  };
  const addTag = () =>
    setFormData((prev) => ({ ...prev, tags: [...prev.tags, ""] }));
  const removeTag = (index) => {
    const tags = [...formData.tags];
    tags.splice(index, 1);
    setFormData((prev) => ({ ...prev, tags }));
  };

  // WorkExperience handlers
  const handleChangeExperience = (index, field, value) => {
    const experiences = [...formData.workExperiences];
    experiences[index] = { ...experiences[index], [field]: value };
    setFormData((prev) => ({ ...prev, workExperiences: experiences }));
    setErrors((prev) => ({ ...prev, workExperiences: "" }));
  };
  const addExperience = () =>
    setFormData((prev) => ({
      ...prev,
      workExperiences: [
        ...prev.workExperiences,
        { organization: "", startYear: "", endYear: "", isCurrent: false },
      ],
    }));
  const removeExperience = (index) => {
    const experiences = [...formData.workExperiences];
    experiences.splice(index, 1);
    setFormData((prev) => ({ ...prev, workExperiences: experiences }));
  };

  const handleSelectChange = (selected) => {
    setFormData((prev) => ({ ...prev, subjectId: Number(selected.value) }));
    setErrors((prev) => ({ ...prev, subjectId: "" }));
  };

  // Validation logic
  const validateForm = () => {
    const newErrors = {};
    if (!formData.subjectId) newErrors.subjectId = "Subject is required.";
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email))
      newErrors.email = "Email is invalid.";
    if (!editData && !formData.password.trim())
      newErrors.password = "Password is required.";
    if (formData.password && formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters.";
    if (!formData.tags.length || formData.tags.some((t) => !t.trim()))
      newErrors.tags = "All tags must be filled.";
    if (!formData.bio.trim()) newErrors.bio = "About Us is required.";
    if (!formData.experienceYears.trim()) {
      newErrors.experienceYears = "Experience years is required.";
    } else if (
      isNaN(formData.experienceYears) ||
      Number(formData.experienceYears) < 0
    ) {
      newErrors.experienceYears =
        "Experience years must be a valid non-negative number.";
    }

    if (!formData.workExperiences.length)
      newErrors.workExperiences = "At least one work experience is required.";
    if (!formData.highlights.trim())
      newErrors.highlights = "Educational Highlights are required.";
    if (!formData.displayOrder.trim())
      newErrors.displayOrder = "Display Order is required.";
    else if (isNaN(formData.displayOrder) || Number(formData.displayOrder) <= 0)
      newErrors.displayOrder = "Display Order must be a positive number.";

    if (!formData.files) newErrors.files = "Teacher image is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const fd = new FormData();
    fd.append("subjectId", formData.subjectId);
    fd.append("name", formData.name);
    fd.append("email", formData.email);
    if (formData.password) fd.append("password", formData.password);

    fd.append("tags", JSON.stringify(formData.tags));
    fd.append("highlights", JSON.stringify(formData.highlights.split("\n")));

    fd.append("bio", formData.bio);
    fd.append("experienceYears", formData.experienceYears);
    fd.append("displayOrder", formData.displayOrder);

    fd.append(
      "workExperiences",
      JSON.stringify(
        formData.workExperiences.map((we) => ({
          organization: we.organization,
          startYear: we.startYear,
          endYear: we.isCurrent ? null : we.endYear,
          isCurrent: we.isCurrent,
        }))
      )
    );

    fd.append("files", formData.files);

    setIsSubmitLoading(true);
    const action = editData
      ? updateMentor({ id: editData.id, formData: fd })
      : createMentor(fd);

    dispatch(action)
      .unwrap()
      .then(() => {
        addNotification(
          editData
            ? "Teacher updated successfully"
            : "Teacher created successfully",
          "success",
          3000
        );
        onClose();
      })
      .catch((err) => {
        addNotification(err.message || err, "warning", 3000);
      })
      .finally(() => setIsSubmitLoading(false));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        files: file,
        previewUrl: URL.createObjectURL(file), // optional preview
      }));
      setErrors((prev) => ({ ...prev, files: "" }));
    }
  };

  // Memoize selected subject label to avoid expensive find
  const selectedSubject = useMemo(() => {
    if (!formData.subjectId) return null;
    const subj = subjects.find((s) => s.id === formData.subjectId);
    return subj ? { value: subj.id.toString(), label: subj.name } : null;
  }, [formData.subjectId, subjects]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center" fontSize="18px" fontWeight={400}>
          {editData ? "Update Teacher" : "Add Teacher"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Subject */}
          <FormControl isRequired isInvalid={!!errors.subjectId}>
            <FormLabel>Subject</FormLabel>
            <Select
              placeholder="Select Subject"
              value={selectedSubject}
              options={subjects.map((s) => ({
                value: s.id.toString(),
                label: s.name,
              }))}
              onChange={handleSelectChange}
              isReadOnly={!!editData}
              useBasicStyles
            />
            <FormErrorMessage>{errors.subjectId}</FormErrorMessage>
            {editData && (
              <Text fontSize="12px" color="gray.500" mt={1}>
                Subject cannot be edited.
              </Text>
            )}
          </FormControl>

          {/* Name */}
          <FormControl mt={4} isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              name="name"
              placeholder="Enter teacher name"
              value={formData.name}
              onChange={handleChange}
              isReadOnly={!!editData}
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          {/* Email */}
          <FormControl mt={4} isRequired isInvalid={!!errors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              name="email"
              type="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.email}</FormErrorMessage>
          </FormControl>

          {/* Password */}
          {!editData && (
            <FormControl mt={4} isRequired isInvalid={!!errors.password}>
              <FormLabel>Password</FormLabel>
              <Input
                name="password"
                type="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>
          )}

          {/* Tags */}
          <FormControl mt={4} isRequired isInvalid={!!errors.tags}>
            <FormLabel>Tags</FormLabel>
            {formData.tags.map((tag, idx) => (
              <HStack key={idx} mb={2}>
                <Input
                  placeholder="Tag"
                  value={tag}
                  onChange={(e) => handleTagChange(idx, e.target.value)}
                />
                <IconButton
                  aria-label="Remove tag"
                  icon={<MdDeleteOutline />}
                  size="sm"
                  onClick={() => removeTag(idx)}
                  isDisabled={formData.tags.length === 1}
                />
              </HStack>
            ))}
            <Button size="sm" leftIcon={<IoAddOutline />} onClick={addTag}>
              Add Tag
            </Button>
            <FormErrorMessage>{errors.tags}</FormErrorMessage>
          </FormControl>

          {/* About Us */}
          <FormControl mt={4} isRequired isInvalid={!!errors.bio}>
            <FormLabel>Bio</FormLabel>
            <Textarea
              name="bio"
              placeholder="Enter bio"
              value={formData.bio}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.bio}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.experienceYears}>
            <FormLabel>Total Experience (in years)</FormLabel>
            <Input
              name="experienceYears"
              type="number"
              min={0}
              placeholder="Enter years of experience"
              value={formData.experienceYears}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.experienceYears}</FormErrorMessage>
          </FormControl>

          {/* Work Experience */}
          <FormControl mt={4} isInvalid={!!errors.workExperiences}>
            <FormLabel>Work Experience</FormLabel>
            {formData.workExperiences.map((we, idx) => (
              <HStack key={idx} spacing={2} mb={2} align="flex-end">
                <Input
                  placeholder="Organization"
                  value={we.organization}
                  onChange={(e) =>
                    handleChangeExperience(idx, "organization", e.target.value)
                  }
                />
                <Input
                  placeholder="Start Year"
                  value={we.startYear}
                  onChange={(e) =>
                    handleChangeExperience(idx, "startYear", e.target.value)
                  }
                />
                <Input
                  placeholder="End Year"
                  value={we.endYear}
                  onChange={(e) =>
                    handleChangeExperience(idx, "endYear", e.target.value)
                  }
                  isDisabled={we.isCurrent}
                />

                <IconButton
                  icon={<MdDeleteOutline />}
                  aria-label="Delete experience"
                  size="sm"
                  onClick={() => removeExperience(idx)}
                  isDisabled={formData.workExperiences.length === 1}
                />
              </HStack>
            ))}
            <Button
              leftIcon={<IoAddOutline />}
              size="sm"
              onClick={addExperience}
            >
              Add Experience
            </Button>
            <FormErrorMessage>{errors.workExperiences}</FormErrorMessage>
          </FormControl>

          {/* Educational Highlights */}
          <FormControl mt={4} isRequired isInvalid={!!errors.highlights}>
            <FormLabel>Educational Highlights</FormLabel>
            <Textarea
              name="highlights"
              placeholder="Enter highlights, one per line"
              value={formData.highlights}
              onChange={handleChange}
              isReadOnly={!!editData}
            />
            <FormErrorMessage>{errors.highlights}</FormErrorMessage>
          </FormControl>

          {/* Display Order */}
          <FormControl mt={4} isRequired isInvalid={!!errors.displayOrder}>
            <FormLabel>Display Order</FormLabel>
            <Input
              name="displayOrder"
              placeholder="Enter display order"
              value={formData.displayOrder}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.displayOrder}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.files}>
            <FormLabel>Teacher Image</FormLabel>
            <Flex align="center">
              <Box
                flex="1"
                border="1px solid"
                borderColor="gray.300"
                borderRadius="md"
                px={4}
                py={2}
                cursor="pointer"
                onClick={() => inputRef.current.click()}
                bg="white"
              >
                <Text color={formData.files ? "black" : "gray.400"}>
                  {formData.files?.name || "Files to upload"}
                </Text>
              </Box>

              <Button
                ml={2}
                bg={primaryBlue}
                color="white"
                _hover={{ bg: primaryBlueLight }}
                onClick={() => inputRef.current.click()}
                fontWeight={500}
              >
                Upload
              </Button>
            </Flex>

            <Input
              type="file"
              accept="image/*"
              ref={inputRef}
              onChange={handleFileChange}
              display="none"
            />

            {formData.previewUrl && (
              <Image
                src={formData.previewUrl}
                mt={3}
                boxSize="100px"
                objectFit="cover"
                borderRadius="md"
              />
            )}

            <FormErrorMessage>{errors.files}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Flex w="full" justify="center">
            <SubmitButton
              text={editData ? "Update" : "Submit"}
              backColor={primaryBlue}
              textColor="white"
              hoverColor={primaryBlueLight}
              onClickHandler={handleSubmit}
              isLoading={isSubmitLoading}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddTeacherModal;
